import { Injectable, inject } from '@angular/core';
import LogRocket from 'logrocket';
import { Logger } from '../../../services/logger.service';
// → LogRocketLogger at runtime
import { isLocalHost } from '../../../utils/functions';
import {
  ClosedInfo,
  DocumentRenderedInfo,
  DownloadedInfo,
  LoadFailedInfo,
  NormalizedError,
  PreviewOpenedInfo,
  PrintOutcome,
  PrintedInfo,
  RetriedInfo,
  SearchedInfo,
  SizeBucket,
  UnsupportedInfo,
} from './file-preview-telemetry.types';

/** Event-name catalogue. */
export const FilePreviewEvent = {
  Opened: 'FilePreviewOpened',
  FlagEvaluated: 'FilePreviewFlagEvaluated',
  LegacyPathTaken: 'FilePreviewLegacyPathTaken',
  DocumentRendered: 'FilePreviewDocumentRendered',
  LoadFailed: 'FilePreviewLoadFailed',
  FileSwitched: 'FilePreviewFileSwitched',
  Downloaded: 'FilePreviewDownloaded',
  Printed: 'FilePreviewPrinted',
  Searched: 'FilePreviewSearched',
  Retried: 'FilePreviewRetried',
  Unsupported: 'FilePreviewUnsupportedFile',
  Closed: 'FilePreviewClosed',
} as const;

// PII guard: block raw name/query/term/etc. keys, but allow safe derived metrics (termLength, resultCount).
const FORBIDDEN_KEY_PATTERN = /(^|[a-z])(name|query|term|search|title|path|url|email)/i;
const ALLOWED_DERIVED_SUFFIX = /(Length|Count|Bucket|Ms|Id|Kind|Source|Stage|Status)$/;

/**
 * Dialog-scoped telemetry for the file-preview feature. One instance per opened dialog
 * (provided in `FilePreviewComponent.providers`, not root). Owns the per-preview correlation
 * id and timer marks, and emits LogRocket events through a single `emit()` chokepoint.
 */
@Injectable() // dialog-scoped — do NOT add providedIn:'root'
export class FilePreviewTelemetryService {
  private readonly _logger = inject(Logger);

  private readonly _instanceId = `fp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  private readonly _marks = new Map<string, number>();

  constructor() {
    this.mark('open'); // end-to-end timer starts the moment the dialog is constructed
  }

  // ── timing primitives ─────────────────────────────────────────────
  mark(name: string): void {
    this._marks.set(name, performance.now());
  }

  private since(name: string): number | undefined {
    const t = this._marks.get(name);
    return t == null ? undefined : Math.round(performance.now() - t);
  }

  // ── semantic events (called from components) ──────────────────────
  previewOpened(p: PreviewOpenedInfo): void {
    this.emit(FilePreviewEvent.Opened, { ...p, isMultiFile: p.fileCount > 1 });
  }

  documentRendered(p: DocumentRenderedInfo): void {
    this.emit(FilePreviewEvent.DocumentRendered, {
      ...p,
      sizeBucket: this.toSizeBucket(p.sizeBytes),
      fetchMs: this.since('fetch'),
      acquireMs: this.since('acquire'),
      renderMs: this.since('render'),
      endToEndMs: this.since(p.trigger === 'initial' ? 'open' : 'switch'),
    });
  }

  loadFailed(p: LoadFailedInfo): void {
    this.emit(FilePreviewEvent.LoadFailed, {
      ...p,
      ...this.normalizeError(p.error),
    });
  }

  fileSwitched(fromIndex: number, toIndex: number): void {
    this.mark('switch'); // resets the e2e base for the upcoming load
    this.emit(FilePreviewEvent.FileSwitched, { fromIndex, toIndex });
  }

  downloaded(p: DownloadedInfo): void {
    const { sizeBytes, ...rest } = p;
    this.emit(FilePreviewEvent.Downloaded, { ...rest, sizeBucket: this.toSizeBucket(sizeBytes) });
  }

  printed(outcome: PrintOutcome, p: PrintedInfo): void {
    this.emit(FilePreviewEvent.Printed, { ...p, outcome, printMs: this.since('print') });
  }

  /** Engagement signal — length + counts only, never the raw term. */
  searched(p: SearchedInfo): void {
    this.emit(FilePreviewEvent.Searched, { ...p });
  }

  retried(p: RetriedInfo): void {
    this.emit(FilePreviewEvent.Retried, { ...p });
  }

  /** Roadmap signal — emit the file type users wanted but we can't render. */
  unsupported(p: UnsupportedInfo): void {
    this.emit(FilePreviewEvent.Unsupported, { ...p });
  }

  closed(p: ClosedInfo): void {
    this.emit(FilePreviewEvent.Closed, {
      sessionMs: this.since('open'),
      ...p, // closedWhileLoading / closedOnError = abandonment signals
    });
  }

  // ── single fan-out chokepoint ─────────────────────────────────────
  private emit(event: string, props: Record<string, unknown>): void {
    const payload = this.toSafeEventProps({ previewInstanceId: this._instanceId, ...props });
    try {
      // No-op on localhost, where LogRocket isn't initialized.
      if (!isLocalHost()) {
        LogRocket.track(event, payload);
      }
    } catch {
      /* never throw from telemetry */
    }
    this._logger.debug(`FilePreview :: ${event}`, payload); // breadcrumb in the replay timeline
  }

  private toSizeBucket(bytes: number): SizeBucket {
    const mb = bytes / 1_048_576;
    if (mb < 1) return '<1MB';
    if (mb < 5) return '1-5MB';
    if (mb < 25) return '5-25MB';
    if (mb < 100) return '25-100MB';
    if (mb < 250) return '100-250MB';
    return '>250MB';
  }

  /** Normalize an unknown error into a flat, queryable, PII-free shape. */
  private normalizeError(err: unknown): NormalizedError {
    const e = (err ?? {}) as Record<string, unknown>;

    const httpStatus = typeof e['status'] === 'number' ? (e['status'] as number) : undefined;
    // An HTTP status pins the kind regardless of the carrier (plain object or HttpErrorResponse).
    const errorKind = httpStatus !== undefined ? 'HttpError' : err instanceof Error ? err.name : typeof err;

    // HttpErrorResponse.message embeds the signed DMS download URL (token included) — drop it entirely;
    // errorKind + httpStatus already carry the queryable signal. Other errors can still embed the
    // filename/URL (e.g. Apryse load errors), so scrub URL-like substrings before forwarding.
    if (errorKind === 'HttpError') {
      return { errorKind, httpStatus };
    }

    return {
      errorKind,
      httpStatus,
      message: this.scrubMessage(this.getErrorMessage(e)),
    };
  }

  /** Replaces URL / file-path substrings with a placeholder so no signed URL or filename leaks. */
  private scrubMessage(message: string): string {
    return message.replace(/\b[a-z]+:\/\/\S+/gi, '<url>');
  }

  private getErrorMessage(err: Record<string, unknown>): string {
    if (typeof err['message'] === 'string') {
      return err['message'] as string;
    }

    if (typeof err === 'string') {
      return err;
    }

    return 'Unknown error';
  }

  /**
   * Reduces props to a flat map of primitives safe for LogRocket: drops PII-risk keys
   * (name / query / term / …) and any non-primitive value (objects, arrays, null, undefined).
   */
  private toSafeEventProps(props: Record<string, unknown>): Record<string, string | number | boolean> {
    const safe: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(props)) {
      const isForbiddenKey = FORBIDDEN_KEY_PATTERN.test(key) && !ALLOWED_DERIVED_SUFFIX.test(key);
      const isPrimitive = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
      if (!isForbiddenKey && isPrimitive) {
        safe[key] = value;
      }
    }
    return safe;
  }
}
